import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useMemo, useState, useEffect, useRef } from "preact/hooks"

import DesktopQuickAdd from "./DesktopQuickAdd";

import { removePriceDecimal, productImagePlaceholder, getSrc, getSrcset, fetchVideo, sortData } from "../helpers"
import { currency, sizeOrder } from "../config"

export default function Product({ product, setSizeGuideOpen, openMobileQuickAdd, gridView }) {
    const { format } = useCurrencyFormatting();
    const [ video, setVideo ] = useState(null);
    const [ quickAddOpen, setQuickAddOpen ] = useState(false);

    const qaToggleRef = useRef(null);

    const videoId = useMemo(() => product.customFields?.find(f => f.key === 'custom-product_card_video')?.value, []);
    const videoIsMain = useMemo(() => product.customFields?.find(f => f.key === 'custom-show_product_card_video_as_primary_asset')?.value, []);
    const sortedSkus = useMemo(() => sortData(product.skus, sizeOrder, (sku) => sku.customFields?.find(f => f.key === 'size')?.value), []);
    const isPreorder = useMemo(() => product.tags1?.find(t => t.toLowerCase() === 'preorder'), []);

    const tag = useMemo(() => {
        if (product.availability !== 'InStock') return 'Sold Out';
        if (product.inventoryLevel <= 5) return `${product.inventoryLevel} left`
        const customFlags = JSON.parse(product.customFields.find(f => f.key === 'custom-flag')?.value || '[]');
        if (customFlags.length > 0) return customFlags[customFlags.length - 1]
        return null;
    }, [ product ])

    useEffect(async () => {
        if (!videoIsMain || !videoId || video) return; // note: `videoIsMain` must to be 'true' or 'false'
        const videoData = await fetchVideo(videoId);
        setVideo(videoData);
    }, [ videoId, videoIsMain, video ]);

    const handleQuickAdd = () => {
        if(window.innerWidth >= 1240) {
            setQuickAddOpen(prev => !prev);
        } else {
            openMobileQuickAdd(product);
        }
    }

    return (
        <SerpElement
            as="li"
            hit={product}
            ariaLabel={`Product ${product.name}`}
            data-handle={product.url.split('/products/')[1]}
            class={`ns-product ns-block ns-w-100 ${currency !== 'USD' ? 'ns-markets' : ''}`} //keep for currency loader (CSS)
        >
            <div class="ns-product-media ns-block ns-w-100 ns-relative">
                <button
                    class="smartwishlist ns-product-wishlist"
                    type="button"
                    data-product={product.productId}
                    data-variant={product.skus?.[0]?.id}
                />
                <a href={product.url} class="ns-product-link ns-block ns-w-100 ns-h-100">
                    {videoId && videoIsMain === 'true' ? (
                        <>
                        <video
                            playsinline="playsinline" 
                            muted="muted" 
                            loop="loop"
                            autoplay="autoplay"
                            preload="none"
                            class="ns-video ns-absolute ns-block ns-w-100 ns-h-100"
                        >
                            {video?.sources?.map(source => 
                                <source 
                                    key={source.url} 
                                    src={source.url} 
                                    type={source.format === "m3u8" ? "application/x-mpegURL" : `video/${source.format}`} 
                                />
                            )}
                            Your browser does not support the video tag.
                        </video>
                        {gridView > -1 &&
                            <img
                                class="ns-hover-media ns-absolute ns-block ns-w-100 ns-h-100"
                                src={getSrc(product.imageUrl, 300)}
                                srcset={window.innerWidth < 1024
                                    ? getSrcset(product.imageUrl, [200, 450])
                                    : getSrcset(product.imageUrl, [352, 832, 1200, 1920])
                                }
                                // sizes="(min-width:1600px) 2000px, (min-width: 750px) calc(100vw), calc(100vw)"
                                alt={product.name}
                                loading="lazy"
                                width="2000"
                                height="2997"
                            />
                        }
                        </>
                    ) : (videoId && videoIsMain === 'false') ? (
                        <>
                        <img
                            class="ns-main-media ns-absolute ns-block ns-w-100 ns-h-100"
                            src={getSrc(product.imageUrl, 300) ?? productImagePlaceholder}
                            srcset={window.innerWidth < 1024
                                ? getSrcset(product.imageUrl, [200, 450])
                                : getSrcset(product.imageUrl, [352, 832, 1200, 1920])
                            }
                            // sizes="(min-width:1600px) 2000px, (min-width: 750px) calc(100vw), calc(100vw)"
                            alt={product.name}
                            loading="lazy"
                            width="2000"
                            height="2997"
                        />
                        {gridView > -1 && (
                            <video
                                playsinline="playsinline" 
                                muted="muted" 
                                loop="loop"
                                autoplay="autoplay"
                                preload="none"
                                class="ns-hover-media ns-absolute ns-block ns-w-100 ns-h-100"
                            >
                                {video?.sources?.map(source => 
                                    <source 
                                        key={source.url} 
                                        src={source.url} 
                                        type={source.format === "m3u8" ? "application/x-mpegURL" : `video/${source.format}`} 
                                    />
                                )}
                                Your browser does not support the video tag.
                            </video>
                        )}
                        </>
                    ) : (
                        <>
                        <img
                            class="ns-main-media ns-absolute ns-block ns-w-100 ns-h-100"
                            src={getSrc(product.imageUrl, 300) ?? productImagePlaceholder}
                            srcset={window.innerWidth < 1024
                                ? getSrcset(product.imageUrl, [200, 450])
                                : getSrcset(product.imageUrl, [352, 832, 1200, 1920])
                            }
                            // sizes="(min-width:1600px) 2000px, (min-width: 750px) calc(100vw), calc(100vw)"
                            alt={product.name}
                            loading="lazy"
                            width="2000"
                            height="2997"
                        />
                        {(gridView > -1 && product.alternateImageUrls[0]) && (
                            <img
                                class="ns-hover-media ns-absolute ns-block ns-w-100 ns-h-100"
                                src={getSrc(product.alternateImageUrls[0], 300)}
                                srcset={window.innerWidth < 1024
                                    ? getSrcset(product.alternateImageUrls[0], [200, 450])
                                    : getSrcset(product.alternateImageUrls[0], [352, 832, 1200, 1920])
                                }
                                // sizes="(min-width:1600px) 2000px, (min-width: 750px) calc(100vw), calc(100vw)"
                                alt={product.name}
                                loading="lazy"
                                width="2000"
                                height="2997"
                            />
                        )}
                        </>
                    )}
                </a>
                {product.availability !== 'InStock' ? (
                    <a 
                        href={product.url + '#bis'} 
                        class="ns-product-notify ns-absolute ns-flex ns-items-center ns-justify-center ns-flex-nowrap"
                    >
                        <span class="ns-notify-text">Notify Me</span>
                    </a>
                ) : (
                    <>
                        <button
                            type="button" 
                            class="ns-quickadd-toggle ns-absolute ns-flex ns-items-center ns-justify-center ns-flex-nowrap"
                            aria-expanded={window.innerWidth < 1240 ? false : quickAddOpen}
                            aria-controls={`ns-quickadd-${product.productId}`}
                            onClick={() => handleQuickAdd()}
                            ref={qaToggleRef}
                        >
                            <span class="ns-quickadd-toggle-text">Quick Add</span>
                            <span class="ns-lg-hidden" aria-hidden="true" role="presentation">+</span>
                        </button>
                        {window.innerWidth >= 768 && ( // medium to large devices only
                            <DesktopQuickAdd
                                open={quickAddOpen}
                                setOpen={setQuickAddOpen}
                                productId={product.productId}
                                sortedSkus={sortedSkus}
                                isPreorder={isPreorder}
                                setSizeGuideOpen={setSizeGuideOpen}
                                toggleRef={qaToggleRef}
                            />
                        )}
                    </>
                )}
            </div>
            <div class="ns-product-info" data-nosto-element="product">
                {tag &&
                    <p class="ns-product-tag">{tag}</p>
                }
                <a href={product.url} class="ns-product-link">
                    <h4 class="ns-product-title">
                        {product.name.split(' - ')[0]}
                    </h4>
                    <div class="ns-product-price-wrap">
                        <span class="ns-product-price" aria-label="Price">
                            {removePriceDecimal(format(product.price))}
                        </span>
                        {(product.listPrice && product.listPrice > product.price) && (
                            <s class="ns-product-list-price" aria-label="Compare price">
                                {removePriceDecimal(format(product.listPrice))}
                            </s>
                        )}
                    </div>
                </a>
            </div>
        </SerpElement>
    )
}
